import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'vmd_cookie_consent';

export default function CookieBanner() {
  const [visible,  setVisible]  = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) setVisible(true);

    const mq = window.matchMedia('(max-width: 540px)');
    setIsMobile(mq.matches);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Spazio in fondo alla pagina finché il banner è aperto, così non copre i link del footer
  useEffect(() => {
    document.body.style.paddingBottom = visible ? (isMobile ? '9rem' : '6.5rem') : '';
    return () => { document.body.style.paddingBottom = ''; };
  }, [visible, isMobile]);

  const accept = () => { localStorage.setItem(STORAGE_KEY, 'accepted'); setVisible(false); };
  const decline = () => { localStorage.setItem(STORAGE_KEY, 'declined'); setVisible(false); };

  const baseCard = {
    position: 'fixed', zIndex: 9000,
    background: 'rgba(13,13,24,0.95)',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,58,237,0.15)',
  };

  const btnRifiuta = {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '0.85rem',
    color: 'var(--color-muted)', cursor: 'pointer',
    transition: 'border-color 0.2s, color 0.2s',
  };

  const btnAccetta = {
    background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
    border: 'none', borderRadius: '8px',
    fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.85rem',
    color: 'white', cursor: 'pointer',
    transition: 'opacity 0.2s',
    boxShadow: '0 0 20px rgba(124,58,237,0.35)',
  };

  return (
    <AnimatePresence>
      {visible && (
        isMobile ? (
          /* ── Mobile: colonna, bottom ── */
          <motion.div
            key="banner-mobile"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{   opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              ...baseCard,
              bottom: '1rem',
              left: '1rem', right: '1rem',
              padding: '0.75rem 0.9rem',
              display: 'flex', flexDirection: 'column', gap: '0.6rem',
            }}
          >
            {/* Riga icona + testo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Cookie size={16} color="var(--color-accent-mid)" style={{ flexShrink: 0 }} />
              <p style={{
                flex: 1, margin: 0,
                fontFamily: 'var(--font-body)', fontSize: '0.8rem',
                color: 'var(--color-text-2)', lineHeight: 1.4,
              }}>
                Solo cookie tecnici.{' '}
                <Link to="/policy" style={{ color: 'var(--color-accent-mid)', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                  Privacy policy
                </Link>
              </p>
            </div>
            {/* Pulsanti a larghezza piena */}
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={decline} style={{ ...btnRifiuta, flex: 1, minHeight: 44 }}>Rifiuta</button>
              <button onClick={accept}  style={{ ...btnAccetta, flex: 1, minHeight: 44 }}>Accetta</button>
            </div>
          </motion.div>
        ) : (
          /* ── Desktop: riga, bottom center ── */
          <motion.div
            key="banner-desktop"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{   opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              ...baseCard,
              bottom: '1.5rem', left: '50%',
              transform: 'translateX(-50%)',
              width: 'min(680px, calc(100vw - 2rem))',
              padding: '1.1rem 1.4rem',
              display: 'flex', alignItems: 'center', gap: '1rem',
            }}
          >
            <Cookie size={18} color="var(--color-accent-mid)" style={{ flexShrink: 0 }} />
            <p style={{
              flex: 1, minWidth: 0, margin: 0,
              fontFamily: 'var(--font-body)', fontSize: '0.875rem',
              color: 'var(--color-text-2)', lineHeight: 1.55,
            }}>
              Usiamo cookie tecnici per il funzionamento del sito.{' '}
              <Link to="/policy" style={{ color: 'var(--color-accent-mid)', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                Privacy policy
              </Link>
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexShrink: 0 }}>
              <button onClick={decline} style={{ ...btnRifiuta, padding: '7px 16px' }}
                onMouseEnter={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.color = 'var(--color-text)'; }}
                onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.color = 'var(--color-muted)'; }}
              >Rifiuta</button>
              <button onClick={accept} style={{ ...btnAccetta, padding: '7px 18px' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >Accetta</button>
            </div>
            <button onClick={decline} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-muted)', padding: '2px', flexShrink: 0,
              display: 'flex', alignItems: 'center', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
              aria-label="Chiudi"
            >
              <X size={16} />
            </button>
          </motion.div>
        )
      )}
    </AnimatePresence>
  );
}
